import { useState, useEffect, useCallback } from 'react';
import Landing from './pages/Landing';
import Apply from './pages/Apply';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PortalLayout from './PortalLayout';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Referrals from './pages/Referrals';
import Earnings from './pages/Earnings';
import Payments from './pages/Payments';
import Tree from './pages/Tree';
import ReferralCode from './pages/ReferralCode';

const API = import.meta.env.VITE_API_URL;

export interface PortalUser {
  affiliate_user_id: string;
  affiliate_id: string;
  email: string;
  display_name: string;
  status: 'onboarding' | 'enrolled' | 'suspended' | 'terminated';
  referral_code: string;
  /** Tier program enabled for this affiliate. Optional for back-compat with older session JSON. */
  tier_enabled?: boolean;
  /** Second-level override commissions enabled. Optional for back-compat. */
  second_level_enabled?: boolean;
  /** Set when this session is an admin impersonation — admin_users.id of the impersonator. */
  impersonated_by?: string;
}

const PUBLIC_PATHS = new Set(['/', '/login', '/apply', '/forgot-password', '/reset-password']);

export default function AffiliatePortalApp() {
  const [path, setPath] = useState<string>(window.location.pathname);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<PortalUser | null>(null);

  const checkSession = useCallback(async () => {
    try {
      const res = await fetch(`${API}/affiliate-portal/session`, { credentials: 'include' });
      const json = (await res.json()) as { success: boolean; authenticated?: boolean; user?: PortalUser };
      if (json.success && json.authenticated && json.user) {
        setUser(json.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'OAK Affiliate Portal';
  }, []);

  useEffect(() => {
    // First check for an impersonation token in the URL (?_imp=...) — if present, accept it,
    // clean the URL, and then run normal session check.
    const params = new URLSearchParams(window.location.search);
    const impToken = params.get('_imp');
    if (impToken) {
      (async () => {
        try {
          await fetch(`${API}/affiliate-portal/accept-impersonation`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: impToken }),
          });
        } catch { /* fall through to session check */ }
        // Clean the URL (remove ?_imp=…)
        window.history.replaceState({}, '', window.location.pathname);
        setPath(window.location.pathname);
        await checkSession();
      })();
    } else {
      checkSession();
    }

    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [checkSession]);

  const navigate = useCallback((newPath: string) => {
    window.history.pushState({}, '', newPath);
    setPath(newPath);
  }, []);

  const handleLogin = (u: PortalUser) => {
    setUser(u);
    navigate('/dashboard');
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/affiliate-portal/logout`, { method: 'POST', credentials: 'include' });
    } catch { /* ignore */ }
    setUser(null);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Loading affiliate portal...</p>
        </div>
      </div>
    );
  }

  const isPublic = PUBLIC_PATHS.has(path);

  // Authenticated routes: redirect to login if not authenticated
  if (!isPublic && !user) {
    navigate('/login');
    return null;
  }

  // Public pages
  if (isPublic) {
    if (path === '/login') return <Login onLogin={handleLogin} navigate={navigate} />;
    if (path === '/apply') return <Apply navigate={navigate} />;
    if (path === '/forgot-password') return <ForgotPassword navigate={navigate} />;
    if (path === '/reset-password') return <ResetPassword navigate={navigate} />;
    // Default '/' is landing. If user is authenticated, send them to dashboard.
    if (user) {
      navigate('/dashboard');
      return null;
    }
    return <Landing navigate={navigate} />;
  }

  // Authenticated pages
  const pageContent = (() => {
    if (path === '/dashboard') return <Dashboard />;
    if (path === '/profile') return <Profile />;
    if (path === '/referrals') return <Referrals />;
    if (path === '/earnings') return <Earnings />;
    if (path === '/payments') return <Payments />;
    if (path === '/tree') return <Tree />;
    if (path === '/referral-code') return <ReferralCode user={user!} />;
    return <Dashboard />;
  })();

  return (
    <PortalLayout user={user!} currentPath={path} navigate={navigate} onLogout={handleLogout}>
      {pageContent}
    </PortalLayout>
  );
}
